import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Sparkles, ArrowRight, Plus, Minus, X } from 'lucide-react';
import { heroPoster, templates } from '../mock';
import { useTranslation } from '../LanguageContext';
import InvitationExperience from '../components/InvitationExperience';

function FAQ() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: 'Per che eventi lo fate?', a: "Qualunque: laurea, battesimo, comunione, cresima, 18°, compleanni, gender reveal, feste bimbi, anniversari, pensioni, inaugurazioni, eventi aziendali." },
    { q: 'Come lo condivido con gli invitati?', a: 'Con un semplice link, via WhatsApp o dove vuoi. Si apre su qualsiasi telefono, senza scaricare nulla.' },
    { q: 'Posso personalizzare tutto?', a: 'Sì: nome, data, luoghi, colori, foto, musica, testi e la scena animata iniziale.' },
    { q: 'Gli invitati possono confermare la presenza?', a: 'Sì, direttamente dall\'invito con un tocco. Ricevi tutte le conferme via WhatsApp.' },
  ];
  return (
    <section id="faq" className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <div className="text-center mb-12">
        <div className="eyebrow mb-5">{t('home.faq.eyebrow')}</div>
        <h2 className="font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05]">
          {t('home.faq.title1')} <span className="italic-serif">{t('home.faq.title2')}</span>.
        </h2>
      </div>
      <div className="space-y-1">
        {faqs.map((f, i) => (
          <div key={i} className="faq-item">
            <button className="w-full flex items-center justify-between py-5 text-left" onClick={() => setOpen(open === i ? -1 : i)}>
              <span className="font-serif text-lg md:text-xl text-[color:var(--ink)] pr-4">{f.q}</span>
              {open === i ? <Minus size={20} className="text-[color:var(--burgundy)]" /> : <Plus size={20} className="text-[color:var(--burgundy)]" />}
            </button>
            {open === i && <div className="pb-6 text-[color:var(--ink-soft)] leading-relaxed">{f.a}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

// Demo invitation used on the home hero when user taps the phone
const DEMO_INVITE = {
  name: 'Demo Invito',
  template_name: 'Anteprima Live',
  image: heroPoster,
  palette: { bg: '#2a1015', accent: '#c9a96e', text: '#f5e6d8' },
  music: 'romantica',
  eventName: 'Il compleanno di Sofia',
  event_name: 'Il compleanno di Sofia',
  celebrant: 'Sofia',
  age: '30 anni',
  date: '2026-09-14',
  time: '18:30',
  location: 'Villa dei Fiori, Roma',
  program: 'Aperitivo 18:30 • Cena 20:00 • Torta a mezzanotte',
  message: "Non un video, un invito che si apre. Prova a scorrere per vedere tutto.",
  dress: 'Elegante',
  whatsapp_number: '',
  font_style: 'serif',
};

function HeroDemoModal({ onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-12 right-0 text-[#f8ede0] p-2 z-10"><X size={24} /></button>
        <InvitationExperience data={DEMO_INVITE} initiallyOpen={false} autoScroll={true} />
        <div className="text-center mt-5 text-[#f8ede0] font-serif">
          <div className="text-sm italic opacity-70">Un esempio dal vivo</div>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [demoOpen, setDemoOpen] = useState(false);

  const touchSteps = [
    { icon: '👈', title: t('home.touch.step1.title'), desc: t('home.touch.step1.desc') },
    { icon: '✨', title: t('home.touch.step2.title'), desc: t('home.touch.step2.desc') },
    { icon: '💛', title: t('home.touch.step3.title'), desc: t('home.touch.step3.desc') },
  ];

  const howItWorks = [
    { n: 1, title: t('home.how.step1.title'), desc: t('home.how.step1.desc') },
    { n: 2, title: t('home.how.step2.title'), desc: t('home.how.step2.desc') },
    { n: 3, title: t('home.how.step3.title'), desc: t('home.how.step3.desc') },
  ];

  const features = [
    { icon: '💌', title: 'Busta animata al tocco', desc: 'La scena che si apre con musica — il tratto distintivo.' },
    { icon: '📱', title: 'Sito coordinato a tema', desc: 'Programma, storia, luoghi e galleria: tutto in tinta.' },
    { icon: '✉️', title: 'Conferme via WhatsApp', desc: 'Ogni RSVP ti arriva direttamente, in ordine e sotto controllo.' },
    { icon: '🎵', title: 'Musica & galleria', desc: 'Il sottofondo giusto e le foto dell\'evento.' },
    { icon: '🔄', title: 'Modifiche quando vuoi', desc: 'Cambi data, orari o dettagli in tempo reale.' },
    { icon: '🔗', title: 'Un semplice link', desc: 'Si apre su ogni telefono. Nessuna app da scaricare.' },
  ];

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-20 md:pb-28">
        <div className="grid md:grid-cols-2 gap-12 md:gap-8 items-center">
          <div className="fade-up">
            <div className="eyebrow mb-6">{t('home.eyebrow')}</div>
            <h1 className="font-serif text-[3rem] md:text-[4.5rem] leading-[1.02] tracking-tight mb-8">
              {t('home.title1')}<br />
              {t('home.title2')} <span className="italic-serif">{t('home.title3')}</span>.
            </h1>
            <p className="text-[color:var(--ink-soft)] text-lg max-w-lg mb-10 leading-relaxed">{t('home.subtitle')}</p>
            <div className="flex flex-wrap gap-4 mb-8">
              <Link to="/crea" className="btn-primary inline-flex items-center gap-2">{t('home.cta.create')} <ArrowRight size={16} /></Link>
              <Link to="/template" className="btn-secondary">{t('home.cta.templates')}</Link>
            </div>
            <div className="flex flex-wrap gap-3">
              <span className="chip inline-flex items-center gap-2"><Clock size={14} /> {t('home.chip.ready')} <b className="text-[color:var(--ink)]">{t('home.chip.minutes')}</b></span>
              <span className="chip inline-flex items-center gap-2"><Sparkles size={14} className="text-[color:var(--burgundy)]" /> {t('home.chip.pay')} <b className="text-[color:var(--ink)]">{t('home.chip.like')}</b></span>
            </div>
          </div>

          <div className="flex justify-center md:justify-end">
            <div>
              <button onClick={() => setDemoOpen(true)} className="phone-frame phone-frame-lg cursor-pointer hover:shadow-2xl transition-shadow" style={{ border: 'none', padding: 8 }}>
                <div className="phone-screen">
                  <img src={heroPoster} alt="" />
                  <div className="name-label">Sofia</div>
                  <div className="tocca-label pulse">{t('home.pill1.tocca')} · {t('home.pill1.si_apre')}</div>
                </div>
              </button>
              <div className="text-center mt-6 eyebrow">{t('home.pill1.tocca')} <span className="divider-dot"></span> {t('home.pill1.si_apre')}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-center mb-14">
          <div className="eyebrow mb-5">{t('home.touch.eyebrow')}</div>
          <h2 className="font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05]">
            {t('home.touch.title1')}<br />{t('home.touch.title2')} <span className="italic-serif">{t('home.touch.title3')}</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-10 md:gap-14">
          {touchSteps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-5xl mb-6">{s.icon}</div>
              <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
              <p className="text-[color:var(--ink-soft)] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-center mb-16">
          <div className="eyebrow mb-5">{t('home.how.eyebrow')}</div>
          <h2 className="font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05]">
            {t('home.how.title1')} <span className="italic-serif">{t('home.how.title2')}</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-10">
          {howItWorks.map((h) => (
            <div key={h.n} className="text-center">
              <div className="step-num mb-3">{h.n}</div>
              <h3 className="font-serif text-2xl mb-3">{h.title}</h3>
              <p className="text-[color:var(--ink-soft)]">{h.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-14">
          <Link to="/crea" className="btn-primary inline-flex items-center gap-2">{t('home.how.start')} <ArrowRight size={16} /></Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-center mb-14">
          <div className="eyebrow mb-5">{t('home.features.eyebrow')}</div>
          <h2 className="font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05] mb-5">
            {t('home.features.title1')}<br />{t('home.features.title2')} <span className="italic-serif">{t('home.features.title3')}</span>.
          </h2>
          <p className="text-[color:var(--ink-soft)] max-w-xl mx-auto text-lg">{t('home.features.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="text-3xl mb-4">{f.icon}</div>
              <h3 className="font-serif text-xl mb-3">{f.title}</h3>
              <p className="text-[color:var(--ink-soft)] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <FAQ />

      <section className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <h2 className="font-serif text-[2.8rem] md:text-[4rem] leading-[1.05] mb-5">
          {t('home.final.title1')} <span className="italic-serif">{t('home.final.title2')}</span>.
        </h2>
        <p className="text-[color:var(--ink-soft)] text-lg mb-10">{t('home.final.subtitle')}</p>
        <Link to="/crea" className="btn-primary">{t('home.cta.create')}</Link>
      </section>

      {demoOpen && <HeroDemoModal onClose={() => setDemoOpen(false)} />}
    </>
  );
}
